import AbstractView from '@/core/view/abstractView';
import styles from './style.module.scss';
import template from './cohortTableView.tpl';
import LogUtil from '@/core/utils/logUtil';
import HelperUtil from '@/core/utils/helperUtil';
import DomUtil from '@/core/utils/domUtil';
import JSONLoader from '@/core/loader/jsonLoader';
import ContentModel from '../../model/contentModel';
import AppStateModel from '../../model/appStateModel';
import MainController from '../../controller/MainController';
import CandleDiagram from './components/candlediagram/candlediagram';
import Collapsible from './components/collapsable/collapsable';
import Arrow from '@/assets/icons/dropdown-grey.svg?raw';
import {gsap} from 'gsap';
import Tooltip from '../../components/tooltip/tooltip'

let genderHandler;
let yearHandler;

export default class CohortTableView extends AbstractView {

    private common: any;
    private animations: any;
    private diagram!: CandleDiagram;
    private collapsible!: Collapsible;
    private year_selection!: HTMLSelectElement;
    private female_select!: HTMLElement;
    private male_select!: HTMLElement;
    private hint_container!: HTMLElement;
    private hint_content!: HTMLElement;

    constructor(id: string, data: any, params: any) {
        super(id, data, params);

        this.common = ContentModel.instance.getCommonData();
        this.animations = this._data.animations;
        this._data.selection.years = HelperUtil.getInterval(this.common.limits.birthyear.min_year, this.common.limits.birthyear.max_year);
        this._template = template({
            data: this._data,
            styles: styles,
            icon: Arrow,
            default: this.common.defaults.input

        });

        genderHandler = event => this.genderInput(event);
        yearHandler = event => this.yearInput(event);
    }

    preload(completeCallback) {
        let dataFiles = [this._data.model['lifespan']];

        function loadLifespanSuccessHandler(result) {
            AppStateModel.birthdayMap = result[0]['birthdayMap'];
            // CohortTableModel.averagesMap = result[1];
            completeCallback();
        }

        function loadDataErrorHandler(error) {
            LogUtil.error('Loading JSON Error. Couldn´t load data.', error);
        }

        JSONLoader.loadAll(dataFiles).then(loadLifespanSuccessHandler.bind(this), loadDataErrorHandler.bind(this));
    }

    init() {

        // set default if no value is present
        if (!AppStateModel.gender) AppStateModel.gender = 'm';
        if (AppStateModel.birthday) this.year_selection.value = AppStateModel.birthday;
        this.diagram.init();
        this.collapsible.init();

        this.update(1, 0);

        // overwrite dynamically selected text with preset to indicate that
        // a gender should be selected, EVEN though it is preselected (above).
        this.hint_content.innerHTML = this._data.selection.hint.all;


        if (AppStateModel.canJumpToStepOnce()) {
            // consume jumpToStepOnce allowance...
        }
    }

    render() {
        super.render();

        this.diagram = new CandleDiagram(this._data);
        this.diagram.appendTo(this._element);

        if (this._element) {
            this.year_selection = this._element.querySelectorAll('select')[0];
            this.year_selection.addEventListener('change', yearHandler);

            this.female_select = this._element.querySelector('#female_select') as HTMLElement;
            this.male_select = this._element.querySelector('#male_select') as HTMLElement;
            this.female_select.addEventListener('click', genderHandler);
            this.male_select.addEventListener('click', genderHandler);

            this.hint_container = this._element.querySelectorAll('.hint')[0] as HTMLElement;
            this.hint_content = this._element.querySelectorAll('.hint > span')[0] as HTMLElement;

            this.collapsible = new Collapsible(this._data);
            let div = document.createElement('div');
            div.className = 'collapsible_container';
            this._element.appendChild(div);
            this.collapsible.appendTo(div);

            const tooltip = new Tooltip(this._data.source_tooltip.link,this._data.source_tooltip.headline,
              this._data.source_tooltip.text );
            const tooltipContainer = this._element.querySelector('div.source_tooltip') as HTMLElement;
            tooltip.appendTo(tooltipContainer);
        }
    }

    yearInput(event, delay = 0.5) {

        // ignore updates on default
        if (event.target.value !== this.common.defaults.input) {

            AppStateModel.birthday = event.target.value;
            this.update(delay);
        }
    }

    genderInput(event, delay = 0) {

        AppStateModel.gender = event.target.attributes['data-gender'].value;
        this.update(delay);
    }

    updateGenderButtons() {

        if (AppStateModel.gender === 'm') {
            DomUtil.addClass(this.male_select, 'active');
            DomUtil.removeClass(this.female_select, 'active');
        }
        else {
            DomUtil.addClass(this.female_select, 'active');
            DomUtil.removeClass(this.male_select, 'active');
        }
    }

    updateHintText(container_fade = 0.3) {

        // reset text, TODO how to reset rotation after spamming clicks? div gets stuck in a slanted position.
        this.hint_content.style.opacity = '1';
        this.hint_content.style.top = '0';

        let autoAlpha = 1;

        if (!AppStateModel.gender && !AppStateModel.birthday) {
            // include hint for all selections
            this.hint_content.innerHTML = this._data.selection.hint.all;
        }
        else if (!AppStateModel.gender) {
            // hint for gender selection
            this.hint_content.innerHTML = this._data.selection.hint.buttons;
        }
        else if (!AppStateModel.birthday) {
            // hint for birthyear selection
            this.hint_content.innerHTML = this._data.selection.hint.select;
        }
        else {
            // default hint content
            this.hint_content.innerHTML = this._data.selection.hint.none;
            autoAlpha = 0;
        }

        gsap.from(this.hint_container, 0.2, {transform: 'rotate(-4deg)'});
        gsap.to(this.hint_container, container_fade, {autoAlpha: autoAlpha});
        gsap.from(this.hint_content, 0.2, {delay: 0.2, top: -20, autoAlpha: 0});
    }

    destroy() {
        this.diagram.destroy();
        this.collapsible.destroy();
        /*
         EventDispatcher.removeMultipleEventListeners(this.backButton, InterfaceUtil.listener.down, startButtonDownHandler);
         */
    }

    update(delay = 0, hint_duration = 0.3) {
        this.updateHintText(hint_duration);

        if (AppStateModel.gender) {

            this.updateGenderButtons();
        }
        if (AppStateModel.gender && AppStateModel.birthday) {

            this.diagram.update(delay);

            // enable sharing for this view/step when values are present
            MainController.setSharing(true);
        }
    }
}
